import React, {
  useState,
  useCallback,
  useImperativeHandle,
  useRef,
  ForwardedRef,
  forwardRef,
  useMemo,
} from 'react';

import classNames from 'classnames';
import { useQuery, useQueryCache } from 'react-query';
import { Modal } from 'src/common/components';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { ModalRefProps } from 'src/common/components/Modal';
import { OrbitIcons } from 'src/common/constants.';

import {
  createCallModule,
  configureFreeSwitchAccount,
  fetchAvailablePhoneNumbers,
  fetchOfficeSchedules,
  fetchCallGroups,
  fetchPlaybackAudios,
  fetchRingStrategies,
  fetchResolutionStrategies,
} from '../api';
import type {
  CreateCallModulePayload,
  AvailablePhoneNumber,
  OfficeSchedule,
  CallGroup,
  PlaybackAudio,
  RingStrategy,
  ResolutionStrategy,
} from '../api';
import { getApiErrorMessage } from '../constants';

interface AddCallModuleModalProps {
  accountId: number;
  isFirstModule?: boolean;
  onModuleCreated: () => void;
}

type PhoneAssignmentMode = 'auto' | 'pool' | 'manual';
type ConfigType = 'agent_route' | 'message_route';

interface RouteFormState {
  configType: ConfigType;
  groupId: number | null;
  ringStrategyId: number | null;
  resolutionStrategyId: number | null;
  greetingPlaybackId: number | null;
  holdMusicPlaybackId: number | null;
  announcePlaybackId: number | null;
  announceFrequency: number;
  maxWaitTime: number;
  maxCallsOnWait: number;
  messagePlaybackId: number | null;
}

interface FormState {
  name: string;
  phoneMode: PhoneAssignmentMode;
  phoneNumber: string;
  phoneNumberId: number | null;
  scheduleId: number | null;
  inOffice: RouteFormState;
  outOfOffice: RouteFormState;
}

const DEFAULT_ROUTE_STATE: RouteFormState = {
  configType: 'agent_route',
  groupId: null,
  ringStrategyId: null,
  resolutionStrategyId: null,
  greetingPlaybackId: null,
  holdMusicPlaybackId: null,
  announcePlaybackId: null,
  announceFrequency: 30,
  maxWaitTime: 300,
  maxCallsOnWait: 10,
  messagePlaybackId: null,
};

const INITIAL_FORM_STATE: FormState = {
  name: '',
  phoneMode: 'auto',
  phoneNumber: '',
  phoneNumberId: null,
  scheduleId: null,
  inOffice: { ...DEFAULT_ROUTE_STATE },
  outOfOffice: { ...DEFAULT_ROUTE_STATE, configType: 'message_route' },
};

const RouteConfigForm: React.FC<{
  title: string;
  state: RouteFormState;
  onChange: (state: RouteFormState) => void;
  groups: CallGroup[];
  ringStrategies: RingStrategy[];
  resolutionStrategies: ResolutionStrategy[];
  audios: PlaybackAudio[];
}> = ({ title, state, onChange, groups, ringStrategies, resolutionStrategies, audios }) => {
  const greetingAudios = audios.filter((a) => a.playback_type === 'greeting');
  const holdAudios = audios.filter((a) => a.playback_type === 'hold_music');
  const announceAudios = audios.filter((a) => a.playback_type === 'announce');
  const messageAudios = audios.filter((a) => a.playback_type === 'message');

  return (
    <div className="rounded border border-white-2.0 bg-white-1.0 p-3">
      <h5 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-2.0">{title}</h5>

      <div className="mb-3">
        <label className="mb-1 block text-xs font-medium text-gray-3.0">Tipo de ruta</label>
        <select
          value={state.configType}
          onChange={(e) => onChange({ ...state, configType: e.target.value as ConfigType })}
          className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
        >
          <option value="agent_route">Ruta a agentes</option>
          <option value="message_route">Mensaje grabado</option>
        </select>
      </div>

      {state.configType === 'agent_route' && (
        <>
          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-gray-3.0">Grupo de agentes</label>
            <select
              value={state.groupId ?? ''}
              onChange={(e) =>
                onChange({ ...state, groupId: e.target.value ? Number(e.target.value) : null })
              }
              className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
            >
              <option value="">Seleccionar grupo...</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-3.0">
                Estrategia de ring
              </label>
              <select
                value={state.ringStrategyId ?? ''}
                onChange={(e) =>
                  onChange({
                    ...state,
                    ringStrategyId: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
              >
                <option value="">Seleccionar...</option>
                {ringStrategies.map((rs) => (
                  <option key={rs.id} value={rs.id}>
                    {rs.name === 'round_robin' ? 'Round Robin' : 'Orden Fijo'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-3.0">Resolución</label>
              <select
                value={state.resolutionStrategyId ?? ''}
                onChange={(e) =>
                  onChange({
                    ...state,
                    resolutionStrategyId: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
              >
                <option value="">Seleccionar...</option>
                {resolutionStrategies.map((rs) => (
                  <option key={rs.id} value={rs.id}>
                    {rs.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-gray-3.0">Audio de saludo</label>
            <select
              value={state.greetingPlaybackId ?? ''}
              onChange={(e) =>
                onChange({
                  ...state,
                  greetingPlaybackId: e.target.value ? Number(e.target.value) : null,
                })
              }
              className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
            >
              <option value="">Seleccionar...</option>
              {greetingAudios.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-3.0">
                Música de espera
              </label>
              <select
                value={state.holdMusicPlaybackId ?? ''}
                onChange={(e) =>
                  onChange({
                    ...state,
                    holdMusicPlaybackId: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
              >
                <option value="">Seleccionar...</option>
                {holdAudios.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-3.0">
                Audio de anuncio
              </label>
              <select
                value={state.announcePlaybackId ?? ''}
                onChange={(e) =>
                  onChange({
                    ...state,
                    announcePlaybackId: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
              >
                <option value="">Seleccionar...</option>
                {announceAudios.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-3.0">
                Frec. anuncio (s)
              </label>
              <input
                type="number"
                min={5}
                value={state.announceFrequency}
                onChange={(e) => onChange({ ...state, announceFrequency: Number(e.target.value) })}
                className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-3.0">
                Espera máx. (s)
              </label>
              <input
                type="number"
                min={30}
                value={state.maxWaitTime}
                onChange={(e) => onChange({ ...state, maxWaitTime: Number(e.target.value) })}
                className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-3.0">
                Llamadas en cola
              </label>
              <input
                type="number"
                min={1}
                value={state.maxCallsOnWait}
                onChange={(e) => onChange({ ...state, maxCallsOnWait: Number(e.target.value) })}
                className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
              />
            </div>
          </div>
        </>
      )}

      {state.configType === 'message_route' && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-3.0">Audio de mensaje</label>
          <select
            value={state.messagePlaybackId ?? ''}
            onChange={(e) =>
              onChange({
                ...state,
                messagePlaybackId: e.target.value ? Number(e.target.value) : null,
              })
            }
            className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
          >
            <option value="">Seleccionar...</option>
            {messageAudios.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export const AddCallModuleModal = forwardRef(
  (
    { accountId, isFirstModule = false, onModuleCreated }: AddCallModuleModalProps,
    ref: ForwardedRef<ModalRefProps>
  ) => {
    const [formData, setFormData] = useState<FormState>({ ...INITIAL_FORM_STATE });
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const modalRef = useRef<ModalRefProps>(null);
    const queryCache = useQueryCache();

    useImperativeHandle(ref, () => ({
      show: () => {
        setFormData({ ...INITIAL_FORM_STATE });
        setStep(1);
        modalRef.current?.show();
      },
      hide: () => modalRef.current?.hide(),
    }));

    const { data: phoneNumbers = [] } = useQuery(
      ['available-phone-numbers'],
      fetchAvailablePhoneNumbers,
      { refetchOnWindowFocus: false }
    );

    const { data: schedules = [] } = useQuery(
      ['office-schedules', accountId],
      () => fetchOfficeSchedules(accountId),
      { refetchOnWindowFocus: false }
    );

    const { data: groups = [] } = useQuery(
      ['call-groups', accountId],
      () => fetchCallGroups(accountId),
      { refetchOnWindowFocus: false }
    );

    const { data: audios = [] } = useQuery(
      ['playback-audios', accountId],
      () => fetchPlaybackAudios(accountId),
      { refetchOnWindowFocus: false }
    );

    const { data: ringStrategies = [] } = useQuery(
      ['ring-strategies'],
      () => fetchRingStrategies(),
      { refetchOnWindowFocus: false }
    );

    const { data: resolutionStrategies = [] } = useQuery(
      ['resolution-strategies', accountId],
      () => fetchResolutionStrategies(accountId),
      { refetchOnWindowFocus: false }
    );

    const buildConfigPayload = (
      route: RouteFormState,
      scheduleType: string
    ): CreateCallModulePayload['configs'][number] => {
      if (route.configType === 'agent_route') {
        return {
          config_type: 'agent_route' as const,
          data: {
            schedule_type: scheduleType as 'in_office' | 'out_of_office',
            group_id: route.groupId!,
            group_order:
              groups.find((g) => g.id === route.groupId)?.participants.map((p) => p.user_id) ?? [],
            ring_strategy_id: route.ringStrategyId!,
            call_resolution_strategy_id: route.resolutionStrategyId!,
            greeting_playback_id: route.greetingPlaybackId!,
            hold_music_playback_id: route.holdMusicPlaybackId!,
            announce_playback_id: route.announcePlaybackId!,
            announce_frequency: route.announceFrequency,
            max_wait_time: route.maxWaitTime,
            max_calls_on_wait: route.maxCallsOnWait,
          },
        };
      } else {
        return {
          config_type: 'message_route' as const,
          data: {
            schedule_type: scheduleType as 'in_office' | 'out_of_office',
            message_playback_id: route.messagePlaybackId!,
          },
        };
      }
    };

    const handleCreate = async (): Promise<void> => {
      setLoading(true);
      try {
        const payload: CreateCallModulePayload = {
          schedule_id: formData.scheduleId!,
          name: formData.name,
          configs: [
            buildConfigPayload(formData.inOffice, 'in_office'),
            buildConfigPayload(formData.outOfOffice, 'out_of_office'),
          ],
        };

        if (formData.phoneMode === 'manual' && formData.phoneNumber) {
          payload.phone_number = formData.phoneNumber;
        } else if (formData.phoneMode === 'pool' && formData.phoneNumberId) {
          payload.phone_number_id = formData.phoneNumberId;
        }

        const result = await createCallModule(accountId, payload);

        if (isFirstModule) {
          try {
            const configResult = await configureFreeSwitchAccount(accountId, {
              module_id: result.id,
            });

            if (
              !configResult.configured ||
              configResult.provision_status.pending_manual.length > 0
            ) {
              const pendingItems = configResult.provision_status.pending_manual;
              Toast.warning(
                pendingItems.length > 0
                  ? `FreeSWITCH requiere configuración manual: ${pendingItems.join(', ')}`
                  : 'FreeSWITCH no se configuró completamente. Revisa la instancia manualmente.'
              );
            }
          } catch (configErr) {
            Toast.warning(
              'La configuración de FreeSWITCH falló. El módulo fue creado pero requiere configuración manual.'
            );
            console.warn('FreeSWITCH configuration failed:', configErr);
          }
        }

        Toast.success('Módulo creado exitosamente');
        queryCache.invalidateQueries(['available-phone-numbers']);
        setFormData({ ...INITIAL_FORM_STATE });
        setStep(1);
        onModuleCreated();
        modalRef.current?.hide();
      } catch (err: unknown) {
        Toast.error(
          getApiErrorMessage(
            err,
            'Error al crear el módulo. Verifica los datos e intenta nuevamente.'
          )
        );
      } finally {
        setLoading(false);
      }
    };

    const isStep1Valid =
      formData.name.trim().length > 0 &&
      (formData.phoneMode === 'auto' ||
        (formData.phoneMode === 'manual' && formData.phoneNumber.trim().length > 0) ||
        (formData.phoneMode === 'pool' && formData.phoneNumberId !== null));

    const isStep2Valid = formData.scheduleId !== null;

    const isStep3Valid = useMemo(() => {
      const route = formData.inOffice;
      if (route.configType === 'agent_route') {
        return (
          route.groupId !== null &&
          route.ringStrategyId !== null &&
          route.resolutionStrategyId !== null &&
          route.greetingPlaybackId !== null &&
          route.holdMusicPlaybackId !== null &&
          route.announcePlaybackId !== null
        );
      } else {
        return route.messagePlaybackId !== null;
      }
    }, [formData.inOffice]);

    const isStep4Valid = useMemo(() => {
      const route = formData.outOfOffice;
      if (route.configType === 'agent_route') {
        return (
          route.groupId !== null &&
          route.ringStrategyId !== null &&
          route.resolutionStrategyId !== null &&
          route.greetingPlaybackId !== null &&
          route.holdMusicPlaybackId !== null &&
          route.announcePlaybackId !== null
        );
      } else {
        return route.messagePlaybackId !== null;
      }
    }, [formData.outOfOffice]);

    return (
      <Modal
        ref={modalRef}
        showFooter={false}
        actionFunc={() => void handleCreate()}
        actionText="Crear"
        cancelText="Cancelar"
      >
        <div className="max-h-[75vh] overflow-y-auto pr-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-2.0/10">
              <Icon name={OrbitIcons.Phone} size={20} className="text-primary-2.0" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-black-1.0">Nuevo Módulo de Llamada</h3>
              <p className="text-xs text-gray-2.0">Paso {step} de 4</p>
            </div>
          </div>

          <div className="flex gap-1 mb-4">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={classNames(
                  'h-1 flex-1 rounded-full',
                  s <= step ? 'bg-primary-2.0' : 'bg-gray-1.0'
                )}
              />
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-3.0">
                  Nombre del módulo<span className="ml-0.5 text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Ej: Línea principal"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded border border-white-2.0 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="mb-2 block text-xs font-medium text-gray-3.0">
                  Asignación de número telefónico
                </label>
                <div className="space-y-2">
                  <label className="flex cursor-pointer items-center gap-2 rounded border border-white-2.0 p-2 hover:bg-white-1.0">
                    <input
                      type="radio"
                      name="phoneMode"
                      checked={formData.phoneMode === 'auto'}
                      onChange={() => setFormData((prev) => ({ ...prev, phoneMode: 'auto' }))}
                    />
                    <div>
                      <p className="text-sm font-medium text-black-1.0">Auto-asignar</p>
                      <p className="text-xs text-gray-2.0">
                        Se asignará el primer número disponible del pool
                      </p>
                    </div>
                  </label>

                  <label className="flex cursor-pointer items-center gap-2 rounded border border-white-2.0 p-2 hover:bg-white-1.0">
                    <input
                      type="radio"
                      name="phoneMode"
                      checked={formData.phoneMode === 'pool'}
                      onChange={() => setFormData((prev) => ({ ...prev, phoneMode: 'pool' }))}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-black-1.0">Seleccionar del pool</p>
                      <p className="text-xs text-gray-2.0 mb-2">
                        {phoneNumbers.length} números disponibles
                      </p>
                      {formData.phoneMode === 'pool' && (
                        <select
                          value={formData.phoneNumberId ?? ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              phoneNumberId: e.target.value ? Number(e.target.value) : null,
                            }))
                          }
                          className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
                        >
                          <option value="">Seleccionar número...</option>
                          {phoneNumbers.map((pn) => (
                            <option key={pn.id} value={pn.id}>
                              {pn.phone_number}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </label>

                  <label className="flex cursor-pointer items-center gap-2 rounded border border-white-2.0 p-2 hover:bg-white-1.0">
                    <input
                      type="radio"
                      name="phoneMode"
                      checked={formData.phoneMode === 'manual'}
                      onChange={() => setFormData((prev) => ({ ...prev, phoneMode: 'manual' }))}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-black-1.0">Ingresar manualmente</p>
                      <p className="text-xs text-gray-2.0 mb-2">
                        Ingresa un número que no esté en el pool
                      </p>
                      {formData.phoneMode === 'manual' && (
                        <input
                          type="text"
                          placeholder="Ej: 7875551234"
                          value={formData.phoneNumber}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, phoneNumber: e.target.value }))
                          }
                          className="w-full rounded border border-white-2.0 px-2 py-1.5 text-sm"
                        />
                      )}
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <label className="mb-2 block text-xs font-medium text-gray-3.0">
                Horario de oficina<span className="ml-0.5 text-red-500">*</span>
              </label>
              {schedules.length === 0 ? (
                <p className="text-sm text-gray-2.0">
                  No hay horarios configurados. Crea uno primero en la sección de Horarios.
                </p>
              ) : (
                <div className="space-y-2">
                  {schedules.map((schedule) => (
                    <label
                      key={schedule.id}
                      className={classNames(
                        'flex cursor-pointer items-center gap-2 rounded border p-3',
                        formData.scheduleId === schedule.id
                          ? 'border-primary-2.0 bg-primary-0.0/10'
                          : 'border-white-2.0 hover:bg-white-1.0'
                      )}
                    >
                      <input
                        type="radio"
                        name="schedule"
                        checked={formData.scheduleId === schedule.id}
                        onChange={() =>
                          setFormData((prev) => ({ ...prev, scheduleId: schedule.id }))
                        }
                      />
                      <div>
                        <p className="text-sm font-medium text-black-1.0">{schedule.name}</p>
                        <p className="text-xs text-gray-2.0">
                          {schedule.time_slots.filter((s) => s.is_open).length} días abiertos
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <RouteConfigForm
              title="Configuración en horario de oficina"
              state={formData.inOffice}
              onChange={(state) => setFormData((prev) => ({ ...prev, inOffice: state }))}
              groups={groups}
              ringStrategies={ringStrategies}
              resolutionStrategies={resolutionStrategies}
              audios={audios}
            />
          )}

          {step === 4 && (
            <RouteConfigForm
              title="Configuración fuera de horario"
              state={formData.outOfOffice}
              onChange={(state) => setFormData((prev) => ({ ...prev, outOfOffice: state }))}
              groups={groups}
              ringStrategies={ringStrategies}
              resolutionStrategies={resolutionStrategies}
              audios={audios}
            />
          )}
        </div>

        <div className="flex justify-between gap-3 mt-6">
          <div>
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="btn btn-ghost flex items-center"
              >
                <Icon name={OrbitIcons.ChevronLeft} size={14} className="mr-1" />
                Anterior
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => modalRef.current?.hide()}
              className="btn btn-ghost"
            >
              Cancelar
            </button>
            {step < 4 ? (
              <button
                type="button"
                disabled={
                  (step === 1 && !isStep1Valid) ||
                  (step === 2 && !isStep2Valid) ||
                  (step === 3 && !isStep3Valid)
                }
                onClick={() => setStep((s) => s + 1)}
                className="btn btn-secondary flex items-center"
              >
                Siguiente
                <Icon name={OrbitIcons.ChevronRight} size={14} className="ml-1" />
              </button>
            ) : (
              <button
                type="button"
                disabled={!isStep4Valid || loading}
                onClick={() => void handleCreate()}
                className="btn btn-secondary flex items-center"
              >
                {loading ? (
                  <>
                    <span className="animate-spin border-t-transparent w-4 h-4 mr-2 border-2 border-white rounded-full" />
                    Creando...
                  </>
                ) : (
                  <>
                    Crear Módulo
                    <Icon name={OrbitIcons.Check} size={14} className="ml-2" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </Modal>
    );
  }
);
